import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, Calculator, Search, ChevronDown } from 'lucide-react';
import { CreateOrderRequest, OrderItem } from '../types';
import { getMenuItems } from '../data/menuItems';

interface CreateOrderModalProps {
  onClose: () => void;
  onSubmit: (orderData: CreateOrderRequest) => Promise<void>;
}

const CreateOrderModal: React.FC<CreateOrderModalProps> = ({ onClose, onSubmit }) => {
  const [tableNumber, setTableNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<Omit<OrderItem, 'id' | 'totalPrice'>[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showMenu, setShowMenu] = useState(false);
  const [menuSearchTerm, setMenuSearchTerm] = useState('');
  const [selectedMenuIndex, setSelectedMenuIndex] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isTableDropdownOpen, setIsTableDropdownOpen] = useState(false);

  const menuItems = getMenuItems();
  
  // Filter by search term and category
  const filteredMenuItems = menuItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(menuSearchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || 
      (selectedCategory === 'main' && (item.name.toLowerCase().includes('biryani') || item.name.toLowerCase().includes('curry') || item.name.toLowerCase().includes('chicken') || item.name.toLowerCase().includes('mutton') || item.name.toLowerCase().includes('butter') || item.name.toLowerCase().includes('dal') || item.name.toLowerCase().includes('paneer') || item.name.toLowerCase().includes('fish'))) ||
      (selectedCategory === 'breads' && (item.name.toLowerCase().includes('roti') || item.name.toLowerCase().includes('naan'))) ||
      (selectedCategory === 'beverages' && (item.name.toLowerCase().includes('tea') || item.name.toLowerCase().includes('coffee') || item.name.toLowerCase().includes('lassi') || item.name.toLowerCase().includes('drink'))) ||
      (selectedCategory === 'extras' && (item.name.toLowerCase().includes('rice') || item.name.toLowerCase().includes('raita') || item.name.toLowerCase().includes('salad') || item.name.toLowerCase().includes('water')));
    
    return matchesSearch && matchesCategory;
  });
  const menuRef = useRef<HTMLDivElement>(null);
  const tableDropdownRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
        setSelectedMenuIndex(null);
      }
      if (tableDropdownRef.current && !tableDropdownRef.current.contains(event.target as Node)) {
        setIsTableDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    console.log('Validating table number:', tableNumber, 'Type:', typeof tableNumber);
    
    if (!tableNumber || tableNumber.trim() === '') {
      newErrors.tableNumber = 'Table number is required';
    }


    if (items.length === 0) {
      newErrors.items = 'At least one item is required';
    }

    items.forEach((item, index) => {
      if (!item.name.trim()) {
        newErrors[`item_${index}_name`] = 'Item name is required';
      }
      if (item.quantity <= 0) {
        newErrors[`item_${index}_quantity`] = 'Quantity must be greater than 0';
      }
      if (item.unitPrice < 0) {
        newErrors[`item_${index}_price`] = 'Price cannot be negative';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const orderData: CreateOrderRequest = {
        customer_name: customerName.trim() || 'Restaurant Customer',
        customer_phone: customerPhone.trim() || undefined,
        customer_email: undefined, // Not collected in form
        table_number: tableNumber,
        order_type: 'dine_in',
        special_instructions: notes.trim() || undefined,
        items: items.map(item => ({
          item_name: item.name.trim(),
          item_category: getItemCategory(item.name),
          quantity: item.quantity,
          unit_price: item.unitPrice,
          special_instructions: item.notes?.trim() || undefined,
        }))
      };

      console.log('Sending order data to API:', orderData);
      await onSubmit(orderData);
    } catch (error) {
      console.error('Error creating order:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addItem = () => {
    setItems([...items, { name: '', quantity: 1, unitPrice: 0, notes: '' }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index: number, field: keyof Omit<OrderItem, 'id' | 'totalPrice'>, value: string | number) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setItems(updatedItems);
  };

  const selectMenuItem = (menuItem: { name: string; price: number }, itemIndex: number) => {
    updateItem(itemIndex, 'name', menuItem.name);
    updateItem(itemIndex, 'unitPrice', menuItem.price);
    setShowMenu(false);
    setMenuSearchTerm('');
    setSelectedMenuIndex(null);
  };

  const selectTable = (tableNum: string) => {
    setTableNumber(tableNum);
    setIsTableDropdownOpen(false);
    // Clear any existing table number error
    if (errors.tableNumber) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.tableNumber;
        return newErrors;
      });
    }
  };

  const getTableDisplayText = () => {
    return tableNumber ? `Table ${tableNumber}` : 'Select Table';
  };

  const getItemCategory = (itemName: string) => {
    const name = itemName.toLowerCase();
    
    // Main course items
    if (name.includes('biryani') || name.includes('curry') || name.includes('chicken') || 
        name.includes('mutton') || name.includes('butter') || name.includes('dal') || 
        name.includes('paneer') || name.includes('fish')) {
      return 'main_course';
    }
    
    // Bread items
    if (name.includes('roti') || name.includes('naan')) {
      return 'bread';
    }
    
    // Beverages
    if (name.includes('tea') || name.includes('coffee') || name.includes('lassi') || 
        name.includes('drink') || name.includes('water')) {
      return 'beverage';
    }
    
    // Extras/Sides
    if (name.includes('rice') || name.includes('raita') || name.includes('salad')) {
      return 'side';
    }
    
    // Default to main course
    return 'main_course';
  };

  const calculateTotal = () => {
    return items.reduce((total, item) => total + (item.quantity * item.unitPrice), 0);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  return (
    <>
      <style>
        {`
          body {
            overflow: hidden;
            margin: 0;
            padding: 0;
          }
        `}
      </style>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]" 
        style={{ 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          margin: 0, 
          padding: '1rem',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          position: 'fixed',
          width: '100vw',
          height: '100vh'
        }}
      >
      <div 
        className="bg-white rounded-2xl shadow-2xl w-[95%] h-[95vh] overflow-hidden relative" 
        style={{ 
          margin: 0,
          backgroundColor: 'white',
          borderRadius: '1rem'
        }}
      >
        {/* Header - Integrated into content */}
        <div className="flex items-center justify-between p-6 bg-primary-500 text-white rounded-t-2xl">
          <h2 className="text-2xl font-bold">Create New Order</h2>
          <button
            onClick={onClose}
            className="p-2 text-white hover:text-primary-100 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col h-full m-0 p-0">
          {/* Customer Information Sub-header */}
          <div className="bg-gray-50 border-b border-gray-200 p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Table Number *
                </label>
                <div className="relative" ref={tableDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsTableDropdownOpen(!isTableDropdownOpen)}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent flex items-center justify-between ${
                      errors.tableNumber ? 'border-red-500' : 'border-gray-300'
                    } ${tableNumber ? 'text-gray-900' : 'text-gray-500'}`}
                  >
                    <span>{getTableDisplayText()}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isTableDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isTableDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {Array.from({ length: 20 }, (_, i) => {
                        const tableNum = String(i + 1).padStart(2, '0');
                        return (
                          <button
                            key={i + 1}
                            type="button"
                            onClick={() => selectTable(tableNum)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 transition-colors ${
                              tableNumber === tableNum ? 'bg-primary-500 text-white' : 'text-gray-700'
                            }`}
                          >
                            Table {tableNum}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {errors.tableNumber && (
                  <p className="mt-1 text-xs text-red-600">{errors.tableNumber}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Name
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter customer name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Special instructions..."
                />
              </div>
            </div>
          </div>

          {/* Three Column Layout */}
          <div className="flex flex-1">
            {/* Left Sidebar - Categories */}
            <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
              {/* Search */}
              <div className="p-4 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={menuSearchTerm}
                    onChange={(e) => setMenuSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Search items..."
                  />
                </div>
              </div>

              {/* Categories */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-2">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between ${
                      selectedCategory === 'all' 
                        ? 'bg-primary-500 text-white' 
                        : 'hover:bg-primary-50 text-gray-700'
                    }`}
                  >
                    <span>All Items</span>
                    <span className={`text-sm ${selectedCategory === 'all' ? 'text-white' : 'text-gray-500'}`}>
                      ({menuItems.length})
                    </span>
                  </button>
                  <button
                    onClick={() => setSelectedCategory('main')}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between ${
                      selectedCategory === 'main' 
                        ? 'bg-primary-500 text-white' 
                        : 'hover:bg-primary-50 text-gray-700'
                    }`}
                  >
                    <span>Main Course</span>
                    <span className={`text-sm ${selectedCategory === 'main' ? 'text-white' : 'text-gray-500'}`}>
                      (8)
                    </span>
                  </button>
                  <button
                    onClick={() => setSelectedCategory('breads')}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between ${
                      selectedCategory === 'breads' 
                        ? 'bg-primary-500 text-white' 
                        : 'hover:bg-primary-50 text-gray-700'
                    }`}
                  >
                    <span>Breads</span>
                    <span className={`text-sm ${selectedCategory === 'breads' ? 'text-white' : 'text-gray-500'}`}>
                      (2)
                    </span>
                  </button>
                  <button
                    onClick={() => setSelectedCategory('beverages')}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between ${
                      selectedCategory === 'beverages' 
                        ? 'bg-primary-500 text-white' 
                        : 'hover:bg-primary-50 text-gray-700'
                    }`}
                  >
                    <span>Beverages</span>
                    <span className={`text-sm ${selectedCategory === 'beverages' ? 'text-white' : 'text-gray-500'}`}>
                      (4)
                    </span>
                  </button>
                  <button
                    onClick={() => setSelectedCategory('extras')}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between ${
                      selectedCategory === 'extras' 
                        ? 'bg-primary-500 text-white' 
                        : 'hover:bg-primary-50 text-gray-700'
                    }`}
                  >
                    <span>Extras</span>
                    <span className={`text-sm ${selectedCategory === 'extras' ? 'text-white' : 'text-gray-500'}`}>
                      (4)
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Center - Menu Items */}
            <div className="flex-1 bg-white">
              <div className="h-full flex flex-col">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-gray-900">Menu Items</h2>
                  <p className="text-gray-600">{filteredMenuItems.length} items available</p>
                </div>
                
                <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(95vh - 300px)' }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredMenuItems.map((menuItem, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                      {/* Item Image */}
                      <div className="h-48 bg-gray-200 relative overflow-hidden">
                        <img 
                          src={menuItem.image} 
                          alt={menuItem.name}
                          className="w-full h-full object-cover"
                          onLoad={() => {
                            console.log(`✅ Image loaded: ${menuItem.name} - ${menuItem.image}`);
                          }}
                          onError={(e) => {
                            console.log(`❌ Image failed to load: ${menuItem.name} - ${menuItem.image}`);
                            // Fallback to placeholder if image fails to load
                            const img = e.currentTarget;
                            const fallback = img.nextElementSibling as HTMLElement;
                            img.style.display = 'none';
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-200" style={{ display: 'none' }}>
                          <div className="text-gray-400 text-4xl">🍽️</div>
                        </div>
                        <div className="absolute top-3 left-3">
                          <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">V</span>
                          </div>
                        </div>
                        <div className="absolute top-3 right-3">
                          <span className="bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-semibold">
                            BESTSELLER
                          </span>
                        </div>
                      </div>

                      <div className="p-4">
                        <h3 className="font-semibold text-lg text-gray-900 mb-2">{menuItem.name}</h3>
                        <p className="text-gray-600 text-sm mb-3">Delicious {menuItem.name.toLowerCase()} prepared with fresh ingredients</p>
                        
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <span className="text-lg font-bold text-gray-900">{formatCurrency(menuItem.price)}</span>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            const newItem = {
                              name: menuItem.name,
                              quantity: 1,
                              unitPrice: menuItem.price,
                              notes: ''
                            };
                            setItems([...items, newItem]);
                          }}
                          className="w-full bg-primary-500 text-white py-2 px-4 rounded-lg hover:bg-primary-600 transition-colors flex items-center justify-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Add to Order
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            </div>

            {/* Right Sidebar - Cart */}
            <div className="w-80 bg-white border-l border-gray-200 flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Order Cart</h3>
                  <span className="bg-primary-100 text-primary-800 text-sm font-medium px-2 py-1 rounded-full">
                    {items.length} item{items.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: 'calc(95vh - 400px)' }}>
                {items.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
                      </svg>
                    </div>
                    <p className="text-gray-500 font-medium">Your cart is empty</p>
                    <p className="text-gray-400 text-sm mt-1">Add items to get started</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {items.map((item, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 text-sm">{item.name}</h4>
                            {item.notes && (
                              <p className="text-xs text-gray-600 mt-1 italic">Note: {item.notes}</p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-500 hover:text-red-700 transition-colors ml-2"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => updateItem(index, 'quantity', Math.max(1, item.quantity - 1))}
                              className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-300"
                            >
                              -
                            </button>
                            <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
                            <button
                              type="button"
                              onClick={() => updateItem(index, 'quantity', item.quantity + 1)}
                              className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-300"
                            >
                              +
                            </button>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">
                            {formatCurrency(item.quantity * item.unitPrice)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Empty Cart Button */}
                {items.length > 0 && (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => setItems([])}
                      className="w-full text-red-600 border border-red-300 py-2 px-4 rounded-lg hover:bg-red-50 transition-colors text-sm"
                    >
                      Empty Cart
                    </button>
                  </div>
                )}
              </div>

              {/* Cart Summary */}
              <div className="border-t border-gray-200 p-4 bg-white sticky bottom-0">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-lg font-semibold text-gray-900">Total</span>
                  <span className="text-xl font-bold text-green-600">
                    {formatCurrency(calculateTotal())}
                  </span>
                </div>
                <div className="space-y-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary-500 text-white py-3 px-4 rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
                  >
                    {isSubmitting ? 'Creating...' : 'Create Order'}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full text-gray-600 border border-gray-300 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>


        </form>
      </div>
      </div>
    </>
  );
};

export default CreateOrderModal;
